import * as React from 'react';
import { t } from '@deltasierra/shared';
import { Well } from '../Well';
import { Button, PuppetInput } from '..';
import './SingleFieldFormWithList.scss';

export type SingleFieldFormListProps = {
    entries: string[];
    onRemove: (entry: string) => void;
};

const SingleFieldFormList: React.FunctionComponent<SingleFieldFormListProps> = ({ entries, onRemove }) => (
    <div className="single-field-form-list">
        {entries.map((entry, index) => (
            <div className="single-field-form-list__item" key={index}>
                <span className="single-field-form-list__item__text" title={entry}>
                    {entry}
                </span>
                <span className="single-field-form-list__item__remove">
                    <Button label={t('COMMON.REMOVE')} size="xs" theme="danger" onClick={() => onRemove(entry)} />
                </span>
            </div>
        ))}
    </div>
);

export type SingleFieldFormWithListProps = {
    title: string;
    label: string;
    validateEntry: (entry: string) => boolean;
    entries: string[];
    informationBox?: React.ReactNode;
    onEntryChange: (entries: string[]) => void;
};

const SingleFieldFormWithList: React.FunctionComponent<SingleFieldFormWithListProps> = ({
    entries,
    informationBox,
    label,
    onEntryChange,
    title,
    validateEntry,
}) => {
    const [currentEntry, setCurrentEntry] = React.useState('');

    const addEntry = React.useCallback(
        (addedEntry: string) => {
            if (!entries.includes(addedEntry)) {
                onEntryChange([...entries, addedEntry]);
            }
            setCurrentEntry('');
        },
        [entries, onEntryChange],
    );

    const removeEntry = React.useCallback(
        (removedEntry: string) => {
            onEntryChange(entries.filter(entry => entry !== removedEntry));
        },
        [entries, onEntryChange],
    );

    const disabled = React.useMemo(() => !validateEntry(currentEntry), [validateEntry, currentEntry]);

    return (
        <Well title={title}>
            <div className={'single-field-form-with-list'}>
                {informationBox && <div className="alert alert-info">{informationBox}</div>}
                <SingleFieldFormList entries={entries} onRemove={removeEntry} />
                <div className="form-group">
                    <label>{label}</label>
                    <div>
                        <PuppetInput
                            style={{ width: '100%' }}
                            value={currentEntry}
                            onTermChange={term => setCurrentEntry(term)}
                        />
                    </div>
                </div>
                <div>
                    <Button disabled={disabled} theme="primary" onClick={() => addEntry(currentEntry)}>
                        <i className={'glyphicon glyphicon-plus'} />
                        {t('COMMON.ADD')}
                    </Button>
                </div>
            </div>
        </Well>
    );
};

SingleFieldFormWithList.displayName = 'SingleFieldFormWithList';

export default SingleFieldFormWithList;
